import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useInitiatePayment } from "../hooks/useInitiatePayment";
import { App, Card, Flex, Image, Modal, Spin, Typography } from "antd";
import { Button } from "../custom";
import { useNavigate } from "react-router-dom";
import { useAtomValue } from "jotai";
import { paymentChannelAtom } from "../utils/store";
import { routes } from "../routes";
import { REMITA_PUBLIC_KEY } from "../utils/constants";

interface PaymentContextType {
  initiatePayment: (data: ProcessPaymentPayloadWithChannel) => void;
  isProcessing: boolean;
}

const PaymentContext = createContext<PaymentContextType | undefined>(undefined);

export const usePayment = () => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error("usePayment must be used within a PaymentProvider");
  }
  return context;
};

interface PaymentProviderProps {
  children: React.ReactNode;
}

export const PaymentProvider: React.FC<PaymentProviderProps> = ({
  children,
}) => {
  const navigate = useNavigate();
  const { notification } = App.useApp();
  const {
    makePayment,
    isLoading,
    paymentChannels,
    isLoadingChannels,
    refetchChannels,
  } = useInitiatePayment();
  const { Text } = Typography;
  const storedPaymentChannel = useAtomValue(paymentChannelAtom);

  const [channelDialogOpen, setChannelDialogOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<string>("");
  const [currentPaymentData, setCurrentPaymentData] =
    useState<ProcessPaymentPayloadWithChannel | null>(null);

  const shouldUseLiveRemita = useMemo(
    () =>
      Boolean(
        paymentChannels?.find(
          (channel) => channel.name?.trim().toLowerCase() === "remita",
        )?.paymentUrl &&
        !paymentChannels
          ?.find((channel) => channel.name?.trim().toLowerCase() === "remita")
          ?.paymentUrl.includes("demo.remita.net"),
      ),
    [paymentChannels],
  );

  useEffect(() => {
    if (!paymentChannels?.length) {
      return;
    }

    const normalizedStoredChannel = storedPaymentChannel?.trim().toLowerCase();
    const storedChannelMatch = paymentChannels.find(
      (channel) =>
        channel.name?.trim().toLowerCase() === normalizedStoredChannel,
    );

    setSelectedChannel((currentChannel) => {
      if (
        currentChannel &&
        paymentChannels.some(
          (channel) =>
            channel.name?.trim().toLowerCase() ===
            currentChannel.trim().toLowerCase(),
        )
      ) {
        return currentChannel;
      }

      return storedChannelMatch?.name ?? paymentChannels[0]?.name ?? "";
    });
  }, [paymentChannels, storedPaymentChannel]);

  const initiatePayment = async (data: ProcessPaymentPayloadWithChannel) => {
    setCurrentPaymentData(data);
    setChannelDialogOpen(true);

    if (!paymentChannels?.length) {
      await refetchChannels();
    }
  };

  const handleConfirmChannel = async () => {
    if (!currentPaymentData) return;
    try {
      const normalizedChannel = selectedChannel.trim().toLowerCase();
      const returnUrl = `${window.location.origin}/payments`;

      const result = await makePayment({
        ...currentPaymentData,
        channel: selectedChannel,
        returnUrl,
      });

      if (normalizedChannel === "remita") {
        const remitaData = result.redirectPayload?.Data as
          | Record<string, any>
          | undefined;
        const requestPayload = result.paymentResponse.requestPayLoad
          ? (JSON.parse(result.paymentResponse.requestPayLoad) as Record<
              string,
              any
            >)
          : undefined;
        const remitaChannelConfig = paymentChannels?.find(
          (channel) => channel.name?.trim().toLowerCase() === "remita",
        );
        const publicKey =
          remitaData?.publicKey ||
          remitaData?.apiKey ||
          remitaData?.key ||
          requestPayload?.IntegrationKey ||
          remitaChannelConfig?.integrationKey ||
          REMITA_PUBLIC_KEY ||
          "";

        if (!publicKey) {
          notification.error({
            message: "Remita Error",
            description:
              "Remita key was not returned by the payment API or channel configuration.",
          });
          return;
        }

        const rrr =
          remitaData?.RRR || remitaData?.rrr || remitaData?.Rrr || "";

        if (!rrr) {
          notification.error({
            message: "Remita Error",
            description: "Remita did not return an RRR for this transaction.",
          });
          return;
        }

        const [firstName = "", ...lastNameParts] = (
          requestPayload?.CustomerName || ""
        )
          .trim()
          .split(" ");

        setChannelDialogOpen(false);
        setCurrentPaymentData(null);

        const remitaSummaryState: RemitaSummaryPaymentState = {
          key: publicKey,
          customerId:
            result.paymentResponse.studentId ||
            result.paymentResponse.applicantId ||
            currentPaymentData.applicantId,
          rrr,
          firstName,
          lastName: lastNameParts.join(" "),
          email: result.paymentResponse.customerEmail,
          phone: result.paymentResponse.customerPhone,
          amount: Number(
            remitaData?.amount || result.paymentResponse.amount || 0,
          ),
          narration:
            result.redirectPayload?.Description ||
            (currentPaymentData.paymentType === 2
              ? "Acceptance Fee Payment"
              : "Application Fee Payment"),
          paymentReference:
            result.paymentResponse.paymentReference ||
            result.paymentResponse.merchantReference ||
            "",
          isLive: shouldUseLiveRemita,
        };

        navigate(routes.dashboard.remitaSummary, {
          state: remitaSummaryState,
        });
        return;
      }

      setChannelDialogOpen(false);
      setCurrentPaymentData(null);
    } catch (_error) {
      // Errors are surfaced from the payment hook notification.
    }
  };

  const contextValue: PaymentContextType = {
    initiatePayment,
    isProcessing: isLoading,
  };

  return (
    <PaymentContext.Provider value={contextValue}>
      {children}
      <Modal
        open={channelDialogOpen}
        centered
        title="Select Payment Channel"
        footer={null}
        onCancel={() => setChannelDialogOpen(false)}
      >
        {isLoadingChannels && !paymentChannels?.length ? (
          <Spin />
        ) : !paymentChannels?.length ? (
          <Typography.Text>
            No payment channels available at the moment.
          </Typography.Text>
        ) : (
          <div>
            {paymentChannels?.map((channel) => (
              <Card
                key={channel.name}
                hoverable
                onClick={() => setSelectedChannel(channel.name)}
                style={{
                  marginBottom: 12,
                  border: "2px solid",
                  borderColor:
                    selectedChannel === channel.name
                      ? "var(--color-primary)"
                      : "var(--color-border)",
                  borderRadius: 8,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
                onMouseEnter={(e) => {
                  if (selectedChannel !== channel.name) {
                    e.currentTarget.style.borderColor = "var(--color-primary)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedChannel !== channel.name) {
                    e.currentTarget.style.borderColor = "var(--color-border)";
                  }
                }}
                styles={{ body: { padding: 12, width: "100%" } }}
              >
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  {channel.imageUrl && (
                    <Image
                      src={channel.imageUrl}
                      alt={channel.name}
                      height={40}
                      preview={false}
                      style={{
                        width: "auto",
                        maxWidth: 150,
                        objectFit: "contain",
                      }}
                    />
                  )}
                  <Text style={{ fontWeight: 600, fontSize: "24px" }}>
                    {channel.name === "Cyperpay" ? "CyberPay" : channel.name}
                  </Text>
                </div>
              </Card>
            ))}
            <Flex justify="flex-end" gap={".75rem"}>
              <Button
                onClick={() => setChannelDialogOpen(false)}
                text="Cancel"
              />
              <Button
                text="Continue"
                onClick={handleConfirmChannel}
                isLoading={isLoading}
                disabled={!selectedChannel || isLoading}
              />
            </Flex>
          </div>
        )}
      </Modal>
    </PaymentContext.Provider>
  );
};
